import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/rbacApi";
import { REACT_APP_API_URL } from "../../../env";

const REDIS_LOGS_ENDPOINT = "/api/dashboard/admin/redis-logs";
const MAX_STREAM_ROWS = 1000;
const MAX_FETCH_LIMIT = 500;

const DEFAULT_FILTERS = {
  bookingRef: "",
  event: "",
  service: "",
  kind: "",
  outcome: "",
  bookingStatus: "",
  lockStatus: "",
  limit: "100",
};

const FILTER_FIELDS = [
  { name: "bookingRef", label: "Booking ref", placeholder: "012016XXXXXX" },
  { name: "event", label: "Event", placeholder: "hotel_booking_succeeded" },
  { name: "kind", label: "Kind", placeholder: "booking_event" },
  { name: "outcome", label: "Outcome", placeholder: "success / failed" },
  { name: "bookingStatus", label: "Booking status", placeholder: "BOOKED" },
  { name: "lockStatus", label: "Lock status", placeholder: "released" },
  { name: "service", label: "Service", placeholder: "booking-service" },
];

const buildApiUrl = (path) =>
  `${REACT_APP_API_URL.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.logs)) return payload.logs;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (typeof payload?.logs === "string") return payload.logs.split(/\r?\n/).filter(Boolean);
  if (typeof payload?.data === "string") return payload.data.split(/\r?\n/).filter(Boolean);

  return [];
};

const parseStreamPayload = (payload) => {
  if (!payload) return [];

  try {
    const parsed = JSON.parse(payload);
    const entries = extractArray(parsed);
    return entries.length ? entries : [parsed];
  } catch (error) {
    return [payload];
  }
};

const getPayloadMessage = (payload) =>
  payload?.message || payload?.msg || payload?.statusMessage || "";

const getStatusPayload = (payload) =>
  payload?.status && typeof payload.status === "object" ? payload.status : payload;

const getRunningState = (payload, fallback) => {
  const status = getStatusPayload(payload);

  if (typeof status?.running === "boolean") return status.running;
  if (typeof status?.isRunning === "boolean") return status.isRunning;
  if (typeof status?.active === "boolean") return status.active;
  if (typeof status?.streaming === "boolean") return status.streaming;
  if (typeof status?.status === "string") {
    return ["running", "started", "active", "streaming"].includes(
      status.status.toLowerCase()
    );
  }

  return fallback;
};

const getConnectionState = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return typeof status?.connected === "boolean" ? status.connected : fallback;
};

const getBufferSize = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return Number.isFinite(Number(status?.bookingBufferSize))
    ? Number(status.bookingBufferSize)
    : fallback;
};

const getMonitorStatusText = (payload, fallback) => {
  const status = getStatusPayload(payload);
  return typeof status?.status === "string" ? status.status : fallback;
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const extractTimeFromText = (text) => {
  const match = text.match(
    /\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/
  );

  return match?.[0] || "";
};

const extractLevelFromText = (text) => {
  const match = text.match(/\b(ERROR|ERR|WARNING|WARN|INFO|DEBUG|TRACE|SUCCESS)\b/i);
  if (!match) return "LOG";

  return match[1].toUpperCase() === "ERR" ? "ERROR" : match[1].toUpperCase();
};

const cleanMessageText = (text, timestamp, level) => {
  let message = text;
  if (timestamp) message = message.replace(timestamp, "");
  if (level) message = message.replace(new RegExp(`\\b${level}\\b`, "i"), "");

  return message.replace(/^[\s:[\]-]+/, "").trim() || text;
};

const getEntryValue = (entry, keys) => {
  for (const key of keys) {
    if (entry?.[key] !== undefined && entry?.[key] !== null && entry?.[key] !== "") {
      return entry[key];
    }
  }

  return "";
};

const normalizeLogEntry = (entry, index, fallbackTimestamp = "") => {
  if (typeof entry === "string") {
    const timestamp = extractTimeFromText(entry) || fallbackTimestamp;
    const level = extractLevelFromText(entry);

    return {
      id: `redis-log-${index}-${timestamp || entry.slice(0, 20)}`,
      timestamp,
      level,
      service: "Redis",
      summary: cleanMessageText(entry, timestamp, level),
      raw: entry,
    };
  }

  const timestamp =
    getEntryValue(entry, ["timestamp", "time", "createdAt", "created_at", "date"]) ||
    fallbackTimestamp;
  const message = getEntryValue(entry, [
    "summary",
    "message",
    "msg",
    "log",
    "error",
    "details",
  ]);
  const id = getEntryValue(entry, ["id", "_id", "logId"]);

  return {
    id: id !== "" ? id : `redis-log-${index}-${timestamp}`,
    timestamp,
    kind: getEntryValue(entry, ["kind"]),
    event: getEntryValue(entry, ["event", "eventName"]),
    level: String(getEntryValue(entry, ["level", "severity"]) || "LOG").toUpperCase(),
    traceId: getEntryValue(entry, ["traceId", "trace_id"]),
    service:
      getEntryValue(entry, ["service", "source", "context", "module"]) || "Redis",
    bookingRef: getEntryValue(entry, ["bookingRef", "booking_ref", "bookingRefNo"]),
    orderId: getEntryValue(entry, ["orderId", "order_id"]),
    paymentId: getEntryValue(entry, ["paymentId", "payment_id"]),
    bookingStatus: getEntryValue(entry, ["bookingStatus", "booking_status"]),
    outcome: getEntryValue(entry, ["outcome"]),
    lockType: getEntryValue(entry, ["lockType", "lock_type"]),
    lockStatus: getEntryValue(entry, ["lockStatus", "lock_status"]),
    deletedKeys: getEntryValue(entry, ["deletedKeys", "deleted_keys"]),
    summary:
      (typeof message === "string" ? message : message && safeStringify(message)) ||
      safeStringify(entry),
    raw: entry,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getLevelBadgeClass = (level) => {
  const normalized = String(level || "").toLowerCase();

  if (normalized.includes("error")) return "bg-danger";
  if (normalized.includes("warn")) return "bg-warning text-dark";
  if (normalized.includes("success")) return "bg-success";
  if (normalized.includes("debug") || normalized.includes("trace")) return "bg-secondary";
  if (normalized.includes("info")) return "bg-info text-dark";

  return "bg-light text-dark border";
};

const getOutcomeBadgeClass = (outcome) => {
  const normalized = String(outcome || "").toLowerCase();
  if (["success", "succeeded", "completed"].includes(normalized)) return "bg-success";
  if (["failed", "failure", "error"].includes(normalized)) return "bg-danger";
  if (["pending", "processing"].includes(normalized)) return "bg-warning text-dark";
  return "bg-secondary";
};

const sanitizeFilters = (filters, includeLimit = true) => {
  const params = {};

  Object.keys(DEFAULT_FILTERS).forEach((key) => {
    if (key === "limit") return;
    const value = String(filters?.[key] ?? "").trim();
    if (value) params[key] = value;
  });

  if (includeLimit) {
    const requestedLimit = Number(filters?.limit);
    if (Number.isFinite(requestedLimit) && requestedLimit > 0) {
      params.limit = Math.min(MAX_FETCH_LIMIT, Math.floor(requestedLimit));
    }
  }

  return params;
};

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || fallback;

const RedisLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FILTERS });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [monitorStatus, setMonitorStatus] = useState({
    running: false,
    connected: null,
    bookingBufferSize: null,
    status: "stopped",
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  const eventSourceRef = useRef(null);
  const streamIndexRef = useRef(0);

  const closeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
    setStreamConnected(false);
  }, []);

  const setRowsFromPayload = useCallback((payload, fallbackRunning) => {
    const entries = extractArray(payload);
    const normalized = entries.map((entry, index) => normalizeLogEntry(entry, index));

    setLogs(normalized);
    setMonitorStatus((current) => ({
      running: getRunningState(payload, fallbackRunning ?? current.running),
      connected: getConnectionState(payload, current.connected),
      bookingBufferSize: getBufferSize(payload, current.bookingBufferSize),
      status: getMonitorStatusText(payload, current.status),
    }));
    setStatusMessage((current) => getPayloadMessage(payload) || current);
    setLastUpdatedAt(new Date().toISOString());
  }, []);

  const fetchLogs = useCallback(
    async (requestedFilters = DEFAULT_FILTERS, fallbackRunning) => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get(REDIS_LOGS_ENDPOINT, {
          params: sanitizeFilters(requestedFilters),
        });
        setRowsFromPayload(response.data, fallbackRunning);
      } catch (err) {
        console.error("Error fetching Redis logs:", err);
        setError(getErrorMessage(err, "Failed to load Redis logs."));
      } finally {
        setLoading(false);
      }
    },
    [setRowsFromPayload]
  );

  const appendStreamEntries = useCallback((entries) => {
    if (!entries.length) return;

    const receivedAt = new Date().toISOString();
    const normalized = entries.map((entry) => {
      streamIndexRef.current += 1;
      return normalizeLogEntry(entry, `stream-${streamIndexRef.current}`, receivedAt);
    });

    setLogs((current) => {
      const knownIds = new Set(current.map((log) => String(log.id)));
      const additions = normalized.filter((log) => {
        const id = String(log.id);
        if (knownIds.has(id)) return false;
        knownIds.add(id);
        return true;
      });
      return [...current, ...additions].slice(-MAX_STREAM_ROWS);
    });
    setLastUpdatedAt(receivedAt);
  }, []);

  const openStream = useCallback(
    (requestedFilters = DEFAULT_FILTERS) => {
      closeStream();
      setError("");

      const streamParams = new URLSearchParams(
        sanitizeFilters(requestedFilters, false)
      ).toString();
      const streamPath = `${REDIS_LOGS_ENDPOINT}/stream${
        streamParams ? `?${streamParams}` : ""
      }`;
      const eventSource = new EventSource(buildApiUrl(streamPath), {
        withCredentials: true,
      });
      const receiveEntries = (event) => {
        setStreamConnected(true);
        setError("");
        appendStreamEntries(parseStreamPayload(event.data));
      };

      eventSourceRef.current = eventSource;
      setIsStreaming(true);
      setStreamConnected(false);

      eventSource.onopen = () => {
        if (eventSourceRef.current === eventSource) {
          setStreamConnected(true);
          setError("");
        }
      };
      eventSource.onmessage = receiveEntries;
      eventSource.addEventListener("redis-log", receiveEntries);
      eventSource.addEventListener("log", receiveEntries);
      eventSource.addEventListener("logs", receiveEntries);
      eventSource.onerror = () => {
        if (eventSourceRef.current !== eventSource) return;

        setStreamConnected(false);
        if (eventSource.readyState === EventSource.CLOSED) {
          setIsStreaming(false);
          setError("Redis log stream disconnected.");
        } else {
          setError("Redis log stream interrupted. Reconnecting...");
        }
      };
    },
    [appendStreamEntries, closeStream]
  );

  const handleStart = async () => {
    setActionLoading(true);
    setError("");

    try {
      const response = await api.post(`${REDIS_LOGS_ENDPOINT}/start`);
      const reconnectStream = isStreaming;
      if (reconnectStream) closeStream();
      setMonitorStatus((current) => ({
        ...current,
        running: true,
        status: "running",
      }));
      setStatusMessage(getPayloadMessage(response.data) || "Redis logging started.");
      await fetchLogs(appliedFilters, true);
      if (reconnectStream) openStream(appliedFilters);
    } catch (err) {
      console.error("Error starting Redis logs:", err);
      setError(getErrorMessage(err, "Failed to start Redis logging."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    setError("");

    try {
      const response = await api.post(`${REDIS_LOGS_ENDPOINT}/stop`);
      closeStream();
      setMonitorStatus((current) => ({
        ...current,
        running: false,
        status: "stopped",
      }));
      setStatusMessage(getPayloadMessage(response.data) || "Redis logging stopped.");
      await fetchLogs(appliedFilters, false);
    } catch (err) {
      console.error("Error stopping Redis logs:", err);
      setError(getErrorMessage(err, "Failed to stop Redis logging."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStreamToggle = () => {
    if (isStreaming) {
      closeStream();
      return;
    }

    openStream(appliedFilters);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    const sanitized = sanitizeFilters(filters);
    const nextFilters = {
      ...filters,
      limit: String(sanitized.limit || DEFAULT_FILTERS.limit),
    };
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    await fetchLogs(nextFilters);
    if (reconnectStream) openStream(nextFilters);
  };

  const handleClearFilters = async () => {
    const nextFilters = { ...DEFAULT_FILTERS };
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    await fetchLogs(nextFilters);
    if (reconnectStream) openStream(nextFilters);
  };

  const handleRefresh = async () => {
    const reconnectStream = isStreaming;

    if (reconnectStream) closeStream();
    await fetchLogs(appliedFilters);
    if (reconnectStream) openStream(appliedFilters);
  };

  useEffect(() => {
    fetchLogs(DEFAULT_FILTERS);

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return logs;

    return logs.filter((log) =>
      [
        log.timestamp,
        log.level,
        log.service,
        log.kind,
        log.event,
        log.traceId,
        log.bookingRef,
        log.orderId,
        log.paymentId,
        log.bookingStatus,
        log.outcome,
        log.lockType,
        log.lockStatus,
        log.summary,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [logs, searchText]);

  const activeFilterCount = useMemo(
    () => Object.keys(sanitizeFilters(appliedFilters, false)).length,
    [appliedFilters]
  );

  const streamLabel = isStreaming
    ? streamConnected
      ? "Live"
      : "Reconnecting"
    : "Offline";

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="fw-bold mb-1">Redis Booking Logs</h3>
              <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                <span>{filteredLogs.length} records</span>
                {activeFilterCount > 0 && <span>{activeFilterCount} active filters</span>}
                {lastUpdatedAt && <span>Updated {formatDateTime(lastUpdatedAt)}</span>}
                {statusMessage && <span>{statusMessage}</span>}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                className={`badge ${monitorStatus.running ? "bg-success" : "bg-secondary"}`}
              >
                Monitor: {monitorStatus.status || (monitorStatus.running ? "running" : "stopped")}
              </span>
              {monitorStatus.connected !== null && (
                <span
                  className={`badge ${
                    monitorStatus.connected ? "bg-success" : "bg-danger"
                  }`}
                >
                  Redis: {monitorStatus.connected ? "Connected" : "Disconnected"}
                </span>
              )}
              {monitorStatus.bookingBufferSize !== null && (
                <span className="badge bg-light text-dark border">
                  Buffer: {monitorStatus.bookingBufferSize}
                </span>
              )}
              <span
                className={`badge ${
                  streamConnected
                    ? "bg-primary"
                    : isStreaming
                    ? "bg-warning text-dark"
                    : "bg-light text-dark border"
                }`}
              >
                Stream: {streamLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleApplyFilters}>
                <div className="row g-2 align-items-end">
                  {FILTER_FIELDS.map((field) => (
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" key={field.name}>
                      <label className="form-label" htmlFor={`redis-log-${field.name}`}>
                        {field.label}
                      </label>
                      <input
                        id={`redis-log-${field.name}`}
                        name={field.name}
                        type="text"
                        className="form-control"
                        value={filters[field.name]}
                        onChange={handleFilterChange}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label" htmlFor="redis-log-limit">
                      Limit
                    </label>
                    <input
                      id="redis-log-limit"
                      name="limit"
                      type="number"
                      className="form-control"
                      min="1"
                      max={MAX_FETCH_LIMIT}
                      value={filters.limit}
                      onChange={handleFilterChange}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-8">
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || actionLoading}
                      >
                        <i className="bi bi-funnel me-1" aria-hidden="true"></i>
                        Apply filters
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleClearFilters}
                        disabled={loading || actionLoading}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              <hr />

              <div className="row g-2 align-items-end mb-3">
                <div className="col-lg-4 col-md-6">
                  <label className="form-label" htmlFor="redis-log-search">
                    Search loaded rows
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search" aria-hidden="true"></i>
                    </span>
                    <input
                      id="redis-log-search"
                      type="search"
                      className="form-control"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Booking ref, event, status, summary"
                    />
                  </div>
                </div>

                <div className="col-lg-8 col-md-6">
                  <div className="d-flex justify-content-lg-end gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleRefresh}
                      disabled={loading || actionLoading}
                    >
                      <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                      Refresh
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleStart}
                      disabled={loading || actionLoading || monitorStatus.running}
                    >
                      <i className="bi bi-play-fill me-1" aria-hidden="true"></i>
                      Start
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleStop}
                      disabled={loading || actionLoading || !monitorStatus.running}
                    >
                      <i className="bi bi-stop-fill me-1" aria-hidden="true"></i>
                      Stop
                    </button>
                    <button
                      type="button"
                      className={`btn ${isStreaming ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={handleStreamToggle}
                      disabled={loading || actionLoading}
                    >
                      <i className="bi bi-broadcast-pin me-1" aria-hidden="true"></i>
                      {isStreaming ? "Disconnect live" : "Connect live"}
                    </button>
                  </div>
                </div>
              </div>

              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col" className="text-nowrap">
                        Time
                      </th>
                      <th scope="col">Event</th>
                      <th scope="col">Booking</th>
                      <th scope="col">Result</th>
                      <th scope="col">Lock</th>
                      <th scope="col">Service</th>
                      <th scope="col">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          Loading Redis logs...
                        </td>
                      </tr>
                    ) : filteredLogs.length > 0 ? (
                      filteredLogs.map((log, index) => (
                        <tr key={`${log.id}-${index}`}>
                          <td className="text-muted">{index + 1}</td>
                          <td className="text-nowrap">{formatDateTime(log.timestamp)}</td>
                          <td style={{ minWidth: "190px" }}>
                            <div className="fw-semibold">{log.event || "-"}</div>
                            <div className="d-flex gap-1 flex-wrap mt-1">
                              {log.kind && (
                                <span className="badge bg-light text-dark border">{log.kind}</span>
                              )}
                              {log.level && log.level !== "LOG" && (
                                <span className={`badge ${getLevelBadgeClass(log.level)}`}>
                                  {log.level}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ minWidth: "170px" }}>
                            <div className="fw-semibold">{log.bookingRef || "-"}</div>
                            {log.orderId && <div className="small">Order: {log.orderId}</div>}
                            {log.paymentId && (
                              <div className="small">Payment: {log.paymentId}</div>
                            )}
                          </td>
                          <td style={{ minWidth: "130px" }}>
                            {log.bookingStatus && (
                              <div>
                                <span className="badge bg-info text-dark">
                                  {log.bookingStatus}
                                </span>
                              </div>
                            )}
                            {log.outcome && (
                              <div className="mt-1">
                                <span className={`badge ${getOutcomeBadgeClass(log.outcome)}`}>
                                  {log.outcome}
                                </span>
                              </div>
                            )}
                            {!log.bookingStatus && !log.outcome && "-"}
                          </td>
                          <td style={{ minWidth: "170px" }}>
                            <div>{log.lockType || "-"}</div>
                            {log.lockStatus && (
                              <div className="small">Status: {log.lockStatus}</div>
                            )}
                            {log.deletedKeys !== "" && log.deletedKeys !== undefined && (
                              <div className="small">Deleted keys: {log.deletedKeys}</div>
                            )}
                          </td>
                          <td style={{ minWidth: "150px" }}>
                            <div>{log.service || "Redis"}</div>
                            {log.traceId && <div className="small text-break">Trace: {log.traceId}</div>}
                          </td>
                          <td style={{ minWidth: "360px", whiteSpace: "pre-wrap" }}>
                            {log.summary || "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No Redis logs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedisLogs;
